import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { motion } from 'framer-motion';
import FeedForm from './components/FeedForm';
import FieldMappingTable from './components/FieldMappingTable';
import { formFeedsApi, MappingOptions } from '../../services/formFeedsApi';
import toast from 'react-hot-toast';

const CreateFeedPage: React.FC = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [step, setStep] = useState<'basic' | 'mapping'>('basic');
  const [basicFormData, setBasicFormData] = useState<any>(null);
  const [mappingOptions, setMappingOptions] = useState<MappingOptions | null>(null);
  const [fieldMappings, setFieldMappings] = useState<Record<string, string>>({});
  const [loadingMappings, setLoadingMappings] = useState(false);

  const handleBasicFormSubmit = async (formData: any) => {
    setBasicFormData(formData);
    setLoadingMappings(true);
    
    try {
      // Fetch mapping options for the selected form
      const options = await formFeedsApi.getMappingOptions(formData.form_id);
      setMappingOptions(options);
      setStep('mapping');
    } catch (error: any) {
      toast.error('Failed to load field mapping options');
      console.error(error);
    } finally {
      setLoadingMappings(false);
    }
  };

  const handleMappingChange = (projectField: string, formField: string) => {
    setFieldMappings((prev) => ({
      ...prev,
      [projectField]: formField,
    }));
  };

  const handleFinalSubmit = async () => {
    // Check if at least one mapping is configured
    const hasMapping = Object.keys(fieldMappings).length > 0;
    if (!hasMapping) {
      toast.error('Please configure at least one field mapping');
      return;
    }

    try {
      setIsLoading(true);
      
      const payload = {
        form_id: Number(basicFormData.form_id),
        name: basicFormData.name,
        target_module: basicFormData.target_module,
        primary_key: basicFormData.primary_key,
        field_mappings: fieldMappings,
        on_submit: basicFormData.on_submit,
        create_or_update: basicFormData.create_or_update,
        do_not_update_if_exists: basicFormData.do_not_update_if_exists,
        is_active: basicFormData.is_active,
      };
      
      await formFeedsApi.create(payload);
      toast.success('Feed created successfully with field mappings');
      navigate('/form-feeds');
    } catch (error: any) {
      console.error('Create feed error:', error.response?.data);
      const errorMsg = error.response?.data?.message || error.response?.data?.errors 
        ? Object.values(error.response.data.errors).flat().join(', ')
        : 'Failed to create feed';
      toast.error(errorMsg);
    } finally {
      setIsLoading(false);
    }
  };

  const handleBack = () => {
    if (step === 'mapping') {
      setStep('basic');
      setMappingOptions(null);
      setFieldMappings({});
    } else {
      navigate('/form-feeds');
    }
  };

  return (
    <div className="h-full w-full flex flex-col">
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-6"
      >
        <button
          onClick={handleBack}
          className="flex items-center gap-2 theme-text-secondary hover:theme-text-primary transition-colors mb-4"
        >
          <ArrowLeft className="w-4 h-4" />
          {step === 'mapping' ? 'Back to Basic Info' : 'Back to Feeds'}
        </button>
        <h1 className="text-2xl font-bold theme-text-primary">
          Create New Feed {step === 'mapping' && '- Configure Field Mappings'}
        </h1>
        <p className="text-sm theme-text-tertiary mt-1">
          {step === 'basic' 
            ? 'Step 1: Configure basic feed settings' 
            : 'Step 2: Map form fields to project request fields'}
        </p>
      </motion.div>

      {step === 'basic' && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="theme-bg-primary rounded-lg shadow-sm border theme-border p-6 max-w-2xl"
        >
          <FeedForm
            onSubmit={handleBasicFormSubmit}
            onCancel={() => navigate('/form-feeds')}
            isLoading={loadingMappings}
            submitButtonText="Next: Configure Mappings"
          />
        </motion.div>
      )}

      {step === 'mapping' && mappingOptions && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="theme-bg-primary rounded-lg shadow-sm border theme-border p-6"
        >
          <div className="mb-6">
            <h2 className="text-lg font-semibold theme-text-primary mb-2">Field Mappings</h2>
            <p className="text-sm theme-text-tertiary">
              Map form fields to project request fields. At least one mapping is required.
            </p>
          </div>

          <FieldMappingTable
            projectRequestFields={mappingOptions.project_request_fields}
            formFields={mappingOptions.form_fields}
            fieldMappings={fieldMappings}
            onMappingChange={handleMappingChange}
          />

          <div className="flex gap-3 mt-6 pt-6 border-t theme-border">
            <button
              onClick={handleFinalSubmit}
              disabled={isLoading || Object.keys(fieldMappings).length === 0}
              className="flex-1 px-6 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isLoading && <Loader2 className="w-4 h-4 animate-spin" />}
              Create Feed
            </button>
            <button
              onClick={() => setStep('basic')}
              disabled={isLoading}
              className="px-6 py-2.5 theme-bg-tertiary hover:theme-bg-quaternary theme-text-primary rounded-lg font-medium transition-colors disabled:opacity-50"
            >
              Back
            </button>
          </div>
        </motion.div>
      )}
    </div>
  );
};

export default CreateFeedPage;
